import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const RevenueGrowthRate = () => {
  // State variables for Revenue Growth Rate calculation
  const [currentRevenue, setCurrentRevenue] = useState(0); // Revenue for the current period
  const [previousRevenue, setPreviousRevenue] = useState(0); // Revenue for the previous period
  const [revenueGrowthRate, setRevenueGrowthRate] = useState(0); // Calculated revenue growth rate

  // Event handlers to update state values
  const handleCurrentRevenueChange = (e) =>
    setCurrentRevenue(parseFloat(e.target.value) || 0);
  const handlePreviousRevenueChange = (e) =>
    setPreviousRevenue(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate revenue growth rate when any input changes
  useEffect(() => {
    if (previousRevenue > 0 && currentRevenue >= 0) {
      const growthRate =
        ((currentRevenue - previousRevenue) / previousRevenue) * 100;
      setRevenueGrowthRate(growthRate);
    } else {
      setRevenueGrowthRate(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [currentRevenue, previousRevenue]);

  return (
    <Layout title="Revenue Growth Rate Calculator" path="revenue-growth-rate">
      <section className="section-padding" id="revenue-growth-rate-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Revenue Growth Rate Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Revenue Growth Rate Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Current Revenue Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="currentRevenue" className="form-label">
                        Current Period Revenue (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Current Period Revenue:</strong> This is the total revenue for the current period. This could be for the current quarter, year, or any other time frame you are measuring."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="currentRevenue"
                        placeholder="Enter current period revenue"
                        value={currentRevenue || ""}
                        onChange={handleCurrentRevenueChange}
                      />
                    </div>

                    {/* Previous Revenue Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="previousRevenue" className="form-label">
                        Previous Period Revenue (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Previous Period Revenue:</strong> This is the total revenue for the previous period you are comparing to. This could be for the previous quarter, year, or any other time frame."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="previousRevenue"
                        placeholder="Enter previous period revenue"
                        value={previousRevenue || ""}
                        onChange={handlePreviousRevenueChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Revenue Growth Rate:{" "}
                        <span className="fw-bold ms-3">
                          {revenueGrowthRate === 0
                            ? "0.00"
                            : revenueGrowthRate.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          %
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Revenue Growth Rate Calculator
                </h3>

                {/* What is a Revenue Growth Rate Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is a Revenue Growth Rate Calculator?
                </h4>
                <p>
                  A **Revenue Growth Rate Calculator** helps you determine the
                  percentage change in your company's revenue over a specific
                  time period. It compares the revenue of the current period to
                  the previous period, giving insight into business growth.
                </p>

                {/* Why Use a Revenue Growth Rate Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use a Revenue Growth Rate Calculator?
                </h4>
                <p>
                  This calculator helps businesses track performance, set
                  realistic growth targets, and make informed decisions
                  regarding financial strategies and planning.
                </p>

                {/* Benefits of Using a Revenue Growth Rate Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a Revenue Growth Rate Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Track Business Performance:</strong> Helps measure
                    whether the business is growing and at what rate.
                  </li>
                  <li>
                    <strong>Set Realistic Growth Targets:</strong> Provides
                    insights into past performance, helping businesses set
                    achievable goals for the future.
                  </li>
                  <li>
                    <strong>Supports Financial Planning:</strong> Assists in
                    budgeting and forecasting based on historical revenue growth
                    trends.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use a Revenue Growth Rate Calculator?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Finance Teams:</strong> To assess financial health
                    and growth trends.
                  </li>
                  <li>
                    <strong>Corporate Management:</strong> To make data-driven
                    decisions about business strategies and growth objectives.
                  </li>
                  <li>
                    <strong>Investors:</strong> To evaluate the growth potential
                    and stability of businesses before making investment
                    decisions.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  What is considered a good revenue growth rate?
                </h4>
                <p>
                  A good revenue growth rate depends on the industry and stage
                  of the company. Typically, a growth rate of 10% to 20% per
                  year is considered healthy for established businesses, while
                  startups may aim for higher growth.
                </p>

                <h4 className="mt-4 mb-3">
                  How can a company improve its revenue growth rate?
                </h4>
                <p>
                  Companies can improve their revenue growth rate by increasing
                  sales, expanding into new markets, launching new products, or
                  enhancing customer retention strategies.
                </p>

                <h4 className="mt-4 mb-3">
                  Is revenue growth rate the only measure of business success?
                </h4>
                <p>
                  While revenue growth is an important indicator, it's crucial
                  to consider other metrics, such as profit margins, customer
                  satisfaction, and operational efficiency, to get a
                  comprehensive view of business success.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default RevenueGrowthRate;
